import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import TierChip from '../shared/TierChip';
import { formatInt, formatDiamonds, formatPct } from '../shared/format';

/**
 * Module 3 — Desktop tier table.
 *
 * 11 rows (Tier 1→10 + Totals). Sticky header. Targets columns render "—"
 * when null (recompute hasn't been backfilled yet — Task #33).
 *
 * Props:
 *  - rows: full set of manager_tier_current_month rows for the current manager,
 *          ordered by tier asc — totals row (tier=0) MUST be the LAST element.
 */
const TierTableDesktop = ({ rows = [] }) => {
  const { t } = useTranslation();
  if (!rows.length) return null;

  // Bring the totals row (tier=0) to the bottom regardless of input order.
  const totals = rows.find(r => r.is_totals_row || r.tier === 0);
  const tierRows = rows.filter(r => !(r.is_totals_row || r.tier === 0)).sort((a, b) => a.tier - b.tier);
  const ordered = totals ? [...tierRows, totals] : tierRows;

  const cellTd = {
    padding: '12px 14px',
    color: TCN.text,
    fontSize: 13,
    borderBottom: `1px solid ${TCN.border}`,
    whiteSpace: 'nowrap',
  };
  const cellTh = {
    ...cellTd,
    color: TCN.textMute,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    background: TCN.surface1,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  };

  return (
    <Card padded style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: TCN.fontSans,
          }}
        >
          <thead>
            <tr>
              <th style={{ ...cellTh, textAlign: 'start' }}>{t('tcn.m3.col.tier', 'Tier')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.creators', 'Creators')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.diamondsMtd', 'Diamonds MTD')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.diamondsLm', 'LM diamonds')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.completion', 'Completion %')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.upgrade', 'Upgrade')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.maintain', 'Maintain')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.validGoLive', 'Valid GL %')}</th>
              <th style={{ ...cellTh, textAlign: 'end' }}>{t('tcn.m3.col.pkPen', 'PK pen %')}</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((r) => {
              const isTotals = r.is_totals_row || r.tier === 0;
              const noActivity = !isTotals && (r.creators_in_tier ?? 0) === 0;
              return (
                <tr
                  key={r.id || (isTotals ? 'totals' : `tier-${r.tier}`)}
                  style={{
                    background: isTotals ? `${TCN.cyan}0d` : 'transparent',
                    opacity: noActivity ? 0.5 : 1,
                  }}
                >
                  <td style={{ ...cellTd, fontWeight: 700 }}>
                    {isTotals ? (
                      <span style={{ color: TCN.cyan, fontWeight: 800, fontSize: 13, letterSpacing: '0.04em' }}>
                        {t('tcn.m3.col.totals', 'TOTAL')}
                      </span>
                    ) : (
                      <TierChip tier={r.tier} size="sm" solid />
                    )}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end' }}>
                    {formatInt(r.creators_in_tier)}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end' }}>
                    {formatDiamonds(r.diamonds_mtd_tier_clear)}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end', color: TCN.textDim }}>
                    {formatDiamonds(r.diamonds_last_month_tier_clear)}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end' }}>
                    {formatPct(r.diamonds_completion_tier_pct)}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end' }}>
                    <span style={{ color: TCN.green, fontWeight: 600 }}>
                      {formatInt(r.upgrade_actual_tier)}
                    </span>
                    {r.upgrade_target_tier != null && (
                      <span style={{ color: TCN.textDim, fontWeight: 500 }}>
                        {' / '}{formatInt(r.upgrade_target_tier)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end' }}>
                    <span style={{ color: TCN.cyan, fontWeight: 600 }}>
                      {formatInt(r.maintain_actual_tier)}
                    </span>
                    {r.maintain_target_tier != null && (
                      <span style={{ color: TCN.textDim, fontWeight: 500 }}>
                        {' / '}{formatInt(r.maintain_target_tier)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end', color: TCN.textDim }}>
                    {formatPct(r.valid_go_live_rate_tier)}
                  </td>
                  <td style={{ ...cellTd, textAlign: 'end', color: TCN.textDim }}>
                    {formatPct(r.pk_penetration_tier)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default TierTableDesktop;
